import { useState } from 'react';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { MemoDetailsDocument } from '@/modules/memos/graphql/MemoDetails.generated';
import { useUpdateMemoMutation } from '@/modules/memos/graphql/UpdateMemo.generated';
import { MemoAiRating } from '@/types/schema';

interface UseMemoRatingParams {
  memoId: string;
  hasExistingFeedback: boolean;
}

export function useMemoRating({
  memoId,
  hasExistingFeedback,
}: UseMemoRatingParams) {
  const { showFeedback } = useFeedback();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const [updateMemoRating, { loading: savingMemoRating }] =
    useUpdateMemoMutation({
      refetchQueries: [MemoDetailsDocument],
      awaitRefetchQueries: true,
      onError: () => {
        showFeedback(FeedbackMessages.formSaveError);
      },
    });

  const closeFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  const handleRateMemo = async (rating: MemoAiRating) => {
    const { data } = await updateMemoRating({
      variables: {
        input: {
          id: memoId,
          update: {
            aiRating: rating,
            clearAiFeedback: rating === MemoAiRating.Good ? true : undefined,
          },
        },
      },
    });

    if (!data?.updateMemo.id) {
      return;
    }

    if (rating === MemoAiRating.Bad && !hasExistingFeedback) {
      setIsFeedbackModalOpen(true);
    }
  };

  const provideMemoFeedback = (feedbackText: string) => {
    return updateMemoRating({
      variables: {
        input: {
          id: memoId,
          update: {
            aiFeedback: feedbackText,
          },
        },
      },
    });
  };

  return {
    isFeedbackModalOpen,
    closeFeedbackModal,
    handleRateMemo,
    provideMemoFeedback,
    savingMemoRating,
  };
}
